import { isTutorialMode } from './mode';
import { isolateBrowserStorage } from './storage';
import { TutorialFixtureMissingError, TutorialIsolationError } from './errors';
import { postTutorialMessage } from './messages';
import type { TutorialBranding } from './session';

/**
 * Isolamento do modo demonstracao — SINCRONO e no import (spec `tutorial-crm`
 * 6.1-1/6.1-2).
 *
 * Este modulo e' o PRIMEIRO import do `main.tsx`. Modulos ES sao avaliados na
 * ordem dos imports, entao tudo aqui roda antes de qualquer store persistido,
 * de `services/api.ts` e do i18next (`@/locales`, cujo detector grava
 * `language-storage`) — que leem e gravam o armazenamento no proprio import.
 * O iframe do tutorial e' da mesma origem da janela real e compartilha esse
 * armazenamento.
 *
 * No modo tutorial: (1) le a marca do tenant real, so leitura; (2) troca
 * `localStorage`/`sessionStorage` por memoria; (3) corta fetch, sendBeacon,
 * WebSocket e EventSource. Se o isolamento falhar, o import LANCA: o app nao e'
 * avaliado nem montado, e a janela real e' avisada.
 *
 * ATENCAO a quem mexer em `manualChunks` (vite.config.ts): modulo do app que
 * le armazenamento no import nao pode ir para um chunk separado do entry —
 * chunk importado pelo entry e' avaliado ANTES dele, e portanto antes daqui.
 */

/** Chave do store de tenant da sessao REAL — so lida, para copiar a marca. */
const REAL_TENANT_STORAGE_KEY = 'opruscrm-tenant';

const BRAND_FIELDS = ['appName', 'logoUrl', 'faviconUrl', 'primaryColor', 'accentColor'] as const;

/**
 * Marca do tenant real (cor, logo, nome), lida do `localStorage` real ANTES do
 * isolamento, em modo so-leitura: o tutorial deve parecer o CRM da pessoa
 * (spec 5.3). JSON corrompido nao bloqueia o tutorial — ele so sai com a marca
 * padrao, e o motivo vai para o console.
 */
export function readRealBranding(storage: Pick<Storage, 'getItem'>): TutorialBranding | null {
  const raw = storage.getItem(REAL_TENANT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const tenant = (JSON.parse(raw) as { state?: { tenant?: Record<string, unknown> | null } }).state?.tenant;
    if (!tenant) return null;
    const branding: Record<string, string> = {};
    for (const field of BRAND_FIELDS) {
      const value = tenant[field];
      if (typeof value === 'string' && value.length > 0) branding[field] = value;
    }
    return Object.keys(branding).length > 0 ? (branding as TutorialBranding) : null;
  } catch (error) {
    // oprus-allow:swallow — marca e' cosmetica; o tutorial abre com a marca padrao.
    console.warn('[tutorial] marca do tenant ilegivel; usando a padrao', error);
    return null;
  }
}

/**
 * Corta toda saida de rede que nao passa pelo axios: `fetch`, `sendBeacon`,
 * `WebSocket` e `EventSource`. O axios ja vai para o adaptador de demonstracao.
 */
export function blockNetwork(win: Window & typeof globalThis = window): void {
  win.fetch = (input: RequestInfo | URL) => {
    const url = typeof input === 'string' ? input : input.toString();
    const error = new TutorialFixtureMissingError({ method: 'FETCH', path: url, url, reason: 'missing' });
    console.error(error.message);
    return Promise.reject(error);
  };
  if (win.navigator && 'sendBeacon' in win.navigator) {
    Object.defineProperty(win.navigator, 'sendBeacon', { configurable: true, value: () => false });
  }
  const refuse = (kind: string) =>
    class {
      constructor() {
        throw new TutorialIsolationError(`${kind} bloqueado no modo demonstracao`);
      }
    };
  win.WebSocket = refuse('WebSocket') as unknown as typeof WebSocket;
  win.EventSource = refuse('EventSource') as unknown as typeof EventSource;
}

/** Os tres passos do isolamento, nesta ordem. Devolve a marca lida antes da troca. */
export function isolateTutorialWindow(win: Window & typeof globalThis = window): TutorialBranding | null {
  const branding = readRealBranding(win.localStorage);
  isolateBrowserStorage(win);
  blockNetwork(win);
  return branding;
}

let isolatedBranding: TutorialBranding | null = null;

/** Marca do tenant real lida no isolamento (o `bootstrap` a aplica na sessao). */
export function getIsolatedBranding(): TutorialBranding | null {
  return isolatedBranding;
}

if (isTutorialMode()) {
  try {
    isolatedBranding = isolateTutorialWindow(window);
  } catch (error) {
    if (window.parent !== window) {
      postTutorialMessage(window.parent, {
        type: 'tutorial:error',
        message: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
}
